"use client";

import { useEffect, useMemo, useState } from "react";
import { getProposals } from "@/features/enterprise-proposals-grid/_logic/actions";
import { useDebounce } from "@/hooks/useDebounce";
import { filterVenues } from "../_logic/compare-selection";
import { meaningfulTokens } from "../_logic/match";
import {
  MIN_PROPOSAL_QUERY_LENGTH,
  buildProposalItems,
  toProposalVenues,
  type ProposalVenue,
} from "../_logic/proposals";
import type { PaletteItem } from "../_logic/types";

/**
 * Short enough to feel typed-through. Not long enough to make a word cost one
 * request: average inter-key intervals sit around 300-400ms, so a word still
 * costs several. Raising it trades latency for server load and is a product
 * call, so it is left where it is and stated honestly rather than overclaimed.
 */
const DEBOUNCE_MS = 250;

/**
 * Deduping to venues costs rows, so ask for more cards than we intend to show.
 * Twenty still covers {@link MAX_PROPOSAL_ROWS} venues at three offers each, and
 * `getProposals` does per-venue cover-photo and preferred-venue work on whatever
 * it returns, none of which these rows render.
 */
const FETCH_LIMIT = 20;

/** One answer from the server, and the query and event it answers. */
interface ProposalSearchAnswer {
  query: string;
  eventId: string | null;
  venues: ProposalVenue[];
  error: string | null;
}

const NO_ANSWER: ProposalSearchAnswer = { query: "", eventId: null, venues: [], error: null };

interface UseProposalSearchResult {
  items: PaletteItem[];
  /** True from the first keystroke of a searchable query until its answer lands. */
  isLoading: boolean;
  error: string | null;
}

/**
 * Searches this event's proposals by venue name, behind the palette's input.
 *
 * Calls `getProposals` directly, the way the compare page's hook does: it is
 * already a server action carrying its own `checkEnterpriseEventAccess`, so the
 * palette needs no new endpoint and cannot surface an event the user could not
 * open anyway. The search runs server-side (venue name or city), so this pages
 * nothing and caps nothing beyond the fetch limit.
 *
 * Scoped to the resolved event rather than every event the user can see. Cross
 * event search is the more useful version and a much more expensive one: there
 * is no endpoint that searches proposals across events, so it would be one
 * request per event on every keystroke. The palette already knows how to ask
 * which event, so the cheap version is not a dead end.
 *
 * `isLoading` covers the debounce window as well as the request. It is what
 * suppresses the zero-result state, and a query that has not been sent yet is
 * not a miss: without it the palette flashed "No results" and logged a false
 * empty query between keystrokes.
 *
 * @param eventId - The resolved event, or null to stay idle.
 * @param query - The raw palette query.
 * @param enabled - False whenever the palette is closed, at rest, or on a nested
 *   page, so browsing the palette never fetches proposals nobody asked for.
 * @returns Palette rows for the matching venues, plus loading and error state.
 * @throws Never. A failed search is reported through `error`.
 */
export function useProposalSearch(eventId: string | null, query: string, enabled: boolean): UseProposalSearchResult {
  // Derived
  const trimmed = query.trim();
  // `useResolvedEventId` hands back whatever the URL says, unvalidated, so a
  // route like `/dashboard/source/abc/...` would ship `NaN` to the action. It is
  // denied there only because `NaN` is falsy inside the access check, which is a
  // coincidence rather than a guard. Check it here and skip the round trip.
  const numericEventId = Number(eventId);
  const hasEvent = !!eventId && Number.isFinite(numericEventId) && numericEventId > 0;
  const isSearchable = enabled && hasEvent && trimmed.length >= MIN_PROPOSAL_QUERY_LENGTH;

  // Hooks
  // Debounced on the searchable query rather than the raw one, so backspacing
  // below the minimum stops the next request instead of scheduling it.
  const debounced = useDebounce(isSearchable ? trimmed : "", DEBOUNCE_MS);

  // States
  // The query AND event the venues answer, stored with them. The event is part
  // of the identity because these rows resolve `{eventId}` at selection time: on
  // a scope change with the same text in the box, the old event's venues would
  // otherwise stay selectable and resolve against the new one, sending the user
  // to a venue that event does not have.
  //
  // The error is stored with them for the same reason. Held in its own state it
  // described whatever was in the box, so one keystroke after a failure reported
  // "Could not search proposals" for a query that had not been sent yet.
  const [result, setResult] = useState<ProposalSearchAnswer>(NO_ANSWER);
  const [isFetching, setIsFetching] = useState(false);

  // Effects
  useEffect(() => {
    if (!hasEvent || !debounced) {
      setResult(NO_ANSWER);
      setIsFetching(false);
      return;
    }

    let cancelled = false;
    setIsFetching(true);

    getProposals({
      eventId: numericEventId,
      tab: "all",
      offset: 0,
      search: debounced,
      sort: { field: "proposalDate", direction: "desc" },
      limit: FETCH_LIMIT,
    })
      .then((page) => {
        if (cancelled) return;
        setResult({ query: debounced, eventId, venues: toProposalVenues(page.items), error: null });
      })
      .catch(() => {
        if (cancelled) return;
        // Said out loud rather than folded into an empty list. A failed search
        // that renders as "no proposals" tells the user their hotel is not in
        // the event, which is the one wrong answer this row can give.
        setResult({ query: debounced, eventId, venues: [], error: "Could not search proposals" });
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debounced, eventId, hasEvent, numericEventId]);

  // Derived
  const isSettling = isSearchable && debounced !== trimmed;
  const isForScope = isSearchable && result.eventId === eventId;
  const isForQuery = isForScope && result.query === trimmed;

  // Memoized so the palette's own `results` memo is not invalidated on every
  // render by a fresh array holding the same six rows.
  const items = useMemo(() => {
    if (!isForScope) return [];
    if (isForQuery) return buildProposalItems(result.venues);
    // Otherwise the previous answer, narrowed to what is typed now. Narrowing
    // rather than clearing is what stops the group blinking out and back on
    // every keystroke, which shoved the highlighted row down the list mid-type.
    //
    // The narrowing is loose, the same matcher the rest of the palette uses:
    // substring or a small edit distance per word, so "milton" still keeps a
    // held "Hilton" until the real answer lands. What it does rule out is a row
    // the query has clearly moved away from.
    //
    // A query with nothing to match on ("!!", or text in a script `toWords`
    // drops, like "东京") clears instead. `filterVenues` treats no tokens as no
    // filter, which held every row through the debounce and the round trip, so
    // Enter opened a venue the box no longer named.
    if (meaningfulTokens(trimmed).length === 0) return [];
    return buildProposalItems(filterVenues(result.venues, trimmed));
  }, [isForQuery, isForScope, result.venues, trimmed]);

  return {
    items,
    // Gated on `isSearchable` so a query backspaced below the minimum stops
    // reporting as loading. Without it the flag stayed true until the debounce
    // re-fired, holding back the zero-result handoff for a query that was never
    // going to be searched.
    //
    // `!isForQuery` covers the commit in which the debounce fires: the query is
    // no longer settling, and the request it starts only flips `isFetching` from
    // that commit's effect. Reporting not-loading there handed the palette a held
    // row count as if it were this query's answer, and announced it.
    isLoading: isSearchable && (isSettling || isFetching || !isForQuery),
    // Only for the query and event that failed. The next keystroke has not been
    // sent yet, so it is loading, not failed.
    error: isForQuery ? result.error : null,
  };
}
